<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';

import IntegrationsAPI from 'dashboard/api/integrations';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();

const integrationLoaded = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const form = ref({
  environment: 'stg',
  server_application_code: '',
  server_app_key: '',
  success_url: '',
  failure_url: '',
  pending_url: '',
  review_url: '',
  allowed_payment_methods: 'All',
  partial_payment: false,
  currency: 'USD',
  country: 'ECU',
  installments_type: 0,
  expiration_days: '',
});

const integration = useFunctionGetter('integrations/getIntegration', 'nuvei');
const hook = computed(() => integration.value?.hooks?.[0] || null);

const populateForm = currentHook => {
  const settings = currentHook?.settings || {};
  const defaults = settings.defaults || {};
  const urls = settings.urls || {};

  form.value = {
    environment: settings.environment || 'stg',
    server_application_code:
      currentHook?.reference_id ||
      settings.connection?.server_application_code ||
      '',
    server_app_key: '',
    success_url: urls.success_url || '',
    failure_url: urls.failure_url || '',
    pending_url: urls.pending_url || '',
    review_url: urls.review_url || '',
    allowed_payment_methods: (defaults.allowed_payment_methods || ['All']).join(
      ', '
    ),
    partial_payment: defaults.partial_payment || false,
    currency: defaults.currency || 'USD',
    country: defaults.country || 'ECU',
    installments_type: defaults.installments_type ?? 0,
    expiration_days: defaults.expiration_days ?? '',
  };
};

const payloadFromForm = () => ({
  environment: form.value.environment,
  server_application_code: form.value.server_application_code,
  ...(form.value.server_app_key
    ? { server_app_key: form.value.server_app_key }
    : {}),
  success_url: form.value.success_url,
  failure_url: form.value.failure_url,
  pending_url: form.value.pending_url,
  review_url: form.value.review_url,
  allowed_payment_methods: form.value.allowed_payment_methods
    .split(',')
    .map(value => value.trim())
    .filter(Boolean),
  partial_payment: form.value.partial_payment,
  currency: form.value.currency,
  country: form.value.country,
  installments_type: Number(form.value.installments_type || 0),
  ...(form.value.expiration_days !== ''
    ? { expiration_days: Number(form.value.expiration_days) }
    : {}),
});

const initialize = async () => {
  await store.dispatch('integrations/get');
  integrationLoaded.value = true;
};

const saveConfiguration = async () => {
  isSaving.value = true;

  try {
    if (hook.value) {
      await IntegrationsAPI.updateNuveiConfig(payloadFromForm());
    } else {
      await IntegrationsAPI.connectNuvei(payloadFromForm());
    }

    await store.dispatch('integrations/get');
    useAlert(t('INTEGRATION_SETTINGS.NUVEI.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('INTEGRATION_SETTINGS.NUVEI.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const disconnect = async () => {
  isDeleting.value = true;

  try {
    await store.dispatch('integrations/deleteIntegration', 'nuvei');
    await store.dispatch('integrations/get');
    useAlert(t('INTEGRATION_SETTINGS.NUVEI.DELETE_SUCCESS'));
  } finally {
    isDeleting.value = false;
  }
};

watch(
  hook,
  currentHook => {
    populateForm(currentHook);
  },
  { immediate: true }
);

onMounted(initialize);
</script>

<template>
  <SettingsLayout :is-loading="!integrationLoaded">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.NUVEI.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.NUVEI.DESCRIPTION')"
        feature-name="integrations"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-6">
        <div
          class="p-6 outline outline-1 outline-n-container rounded-xl bg-n-card flex flex-col gap-6"
        >
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('INTEGRATION_SETTINGS.NUVEI.FIELDS.ENVIRONMENT') }}
              </label>
              <select
                v-model="form.environment"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              >
                <option value="stg">
                  {{ t('INTEGRATION_SETTINGS.NUVEI.OPTIONS.STAGING') }}
                </option>
                <option value="prod">
                  {{ t('INTEGRATION_SETTINGS.NUVEI.OPTIONS.PRODUCTION') }}
                </option>
              </select>
            </div>

            <Input
              v-model="form.server_application_code"
              :label="
                t('INTEGRATION_SETTINGS.NUVEI.FIELDS.SERVER_APPLICATION_CODE')
              "
            />
            <Input
              v-model="form.server_app_key"
              type="password"
              :label="t('INTEGRATION_SETTINGS.NUVEI.FIELDS.SERVER_APP_KEY')"
              :placeholder="
                hook
                  ? t('INTEGRATION_SETTINGS.NUVEI.SERVER_APP_KEY_PLACEHOLDER')
                  : ''
              "
            />
            <Input
              v-model="form.currency"
              :label="t('INTEGRATION_SETTINGS.NUVEI.FIELDS.CURRENCY')"
            />
            <Input
              v-model="form.country"
              :label="t('INTEGRATION_SETTINGS.NUVEI.FIELDS.COUNTRY')"
            />
            <Input
              v-model="form.installments_type"
              type="number"
              :label="t('INTEGRATION_SETTINGS.NUVEI.FIELDS.INSTALLMENTS_TYPE')"
            />
            <Input
              v-model="form.expiration_days"
              type="number"
              :label="t('INTEGRATION_SETTINGS.NUVEI.FIELDS.EXPIRATION_DAYS')"
            />
            <Input
              v-model="form.allowed_payment_methods"
              :label="
                t('INTEGRATION_SETTINGS.NUVEI.FIELDS.ALLOWED_PAYMENT_METHODS')
              "
            />
          </div>

          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input v-model="form.partial_payment" type="checkbox" />
            {{ t('INTEGRATION_SETTINGS.NUVEI.FIELDS.PARTIAL_PAYMENT') }}
          </label>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              v-model="form.success_url"
              :label="t('INTEGRATION_SETTINGS.NUVEI.FIELDS.SUCCESS_URL')"
            />
            <Input
              v-model="form.failure_url"
              :label="t('INTEGRATION_SETTINGS.NUVEI.FIELDS.FAILURE_URL')"
            />
            <Input
              v-model="form.pending_url"
              :label="t('INTEGRATION_SETTINGS.NUVEI.FIELDS.PENDING_URL')"
            />
            <Input
              v-model="form.review_url"
              :label="t('INTEGRATION_SETTINGS.NUVEI.FIELDS.REVIEW_URL')"
            />
          </div>

          <p class="text-sm text-n-slate-11">
            {{ t('INTEGRATION_SETTINGS.NUVEI.HELP') }}
          </p>

          <div class="flex gap-3">
            <Button
              :label="$t('INTEGRATION_SETTINGS.NUVEI.SAVE')"
              :is-loading="isSaving"
              @click="saveConfiguration"
            />
            <Button
              v-if="hook"
              faded
              ruby
              :label="$t('INTEGRATION_SETTINGS.NUVEI.DISCONNECT')"
              :is-loading="isDeleting"
              @click="disconnect"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
